import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  InputAdornment,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { useMutation, useQuery } from "@tanstack/react-query";
import { FormattedMessage, useIntl } from "react-intl";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import AddIcon from "@mui/icons-material/Add";
import FilterAltOffIcon from "@mui/icons-material/FilterAltOff";
import SearchIcon from "@mui/icons-material/Search";
import { useLocalStorage } from "../../hooks/useLocalStorage";
import {
  assignMachineToAssetNode,
  createAssetNode,
  deactivateAssetNode,
  fetchAssetHierarchyMachineCandidates,
  fetchAssetHierarchyTree,
  reparentAssetNode,
  unassignMachineFromAssetNode,
  updateAssetNode,
} from "../../fetchers/cmmsAssetHierarchy";
import AxionControls from "../../components/controls/AxionControls";
import { pushErrorMessage, pushNotification } from "../../utils/notifications";
import AssetHierarchyTable from "./components/AssetHierarchyTable";
import AssetHierarchyToolbar from "./components/AssetHierarchyToolbar";
import AssetHierarchyTree from "./components/AssetHierarchyTree";
import AssetNodeDetailsDrawer from "./components/AssetNodeDetailsDrawer";
import AssetNodeDetailsPanel from "./components/AssetNodeDetailsPanel";
import AssetNodeFormDialog from "./components/AssetNodeFormDialog";
import ReparentDialog from "./components/ReparentDialog";
import cmmsMessages from "./cmmsMessages";
import { CmmsEmptyState, CmmsErrorState, CmmsListSkeleton, formatCmmsError } from "./components/CmmsStates";
import messages from "./messages";
import {
  expandIds,
  flattenAssetNodes,
  getAllowedChildTypeOptions,
  getMaxModelYear,
  isValidModelYear,
  MIN_MODEL_YEAR,
  toggleExpandedId,
} from "./assetHierarchyUtils";
import { filterAssetNodes, getTreeSearchVisibleIds } from "./assetNodeSearch";
import {
  cmmsTypeScale,
  getAssetHierarchyHeaderSx,
  getAssetHierarchyPalette,
  getAssetHierarchyPanelSx,
  getContainedButtonSx,
  getPageHeaderIconSx,
  getPageShellSx,
  getPageSubtitleSx,
  getPageTitleSx,
  getStatLabelSx,
  getStatValueSx,
  getStatisticTileSx,
  getTabsSx,
} from "./assetHierarchyStyles";
import { HideIfNoRole } from "../../auth/authUi";
import { CMMS_MANAGER_ROLES } from "./cmmsRoles";

const countTreeNodes = (nodes) => {
  return (nodes ?? []).reduce((count, node) => count + 1 + countTreeNodes(node.children), 0);
};

export default function CmmsAssetHierarchy() {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const [companyKey] = useLocalStorage("companyKey", 1);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  // 0 = tree, 1 = table. Both render the same query; only the shape differs.
  const [tab, setTab] = useState(0);
  const [searchText, setSearchText] = useState("");
  // Here rather than in the tree, so it survives the tree unmounting on a tab switch. Empty means
  // only the sites show.
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  // Branches collapsed while a search is on. Apart from expandedIds so a search never rewrites
  // the user's own expansion, and cleared with every new query so each one opens all its matches.
  const [searchCollapsedIds, setSearchCollapsedIds] = useState(() => new Set());
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [formMode, setFormMode] = useState(null);
  const [formParentNode, setFormParentNode] = useState(null);
  const [isReparentOpen, setIsReparentOpen] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const treeQuery = useQuery(["cmmsAssetHierarchyTree", companyKey, includeInactive], () => fetchAssetHierarchyTree(companyKey, includeInactive), {
    enabled: Boolean(companyKey),
    staleTime: 120000,
  });

  const machineCandidatesQuery = useQuery(["cmmsAssetHierarchyMachineCandidates", companyKey], () => fetchAssetHierarchyMachineCandidates(companyKey), {
    enabled: Boolean(companyKey),
    staleTime: 120000,
  });

  const refreshAssetData = async () => {
    await Promise.all([treeQuery.refetch(), machineCandidatesQuery.refetch()]);
  };

  const createMutation = useMutation({ mutationFn: (assetNode) => createAssetNode(assetNode) });
  const updateMutation = useMutation({ mutationFn: ({ id, assetNode }) => updateAssetNode(id, assetNode) });
  const reparentMutation = useMutation({ mutationFn: ({ id, newParentAssetNodeId }) => reparentAssetNode(id, companyKey, newParentAssetNodeId) });
  const deactivateMutation = useMutation({ mutationFn: (id) => deactivateAssetNode(companyKey, id) });
  const assignMachineMutation = useMutation({ mutationFn: ({ assetNodeId, machineId }) => assignMachineToAssetNode(companyKey, assetNodeId, machineId) });
  const unassignMachineMutation = useMutation({ mutationFn: (machineId) => unassignMachineFromAssetNode(companyKey, machineId) });

  const isLoading = treeQuery.isLoading || machineCandidatesQuery.isLoading;
  const isRetrying = treeQuery.isFetching || machineCandidatesQuery.isFetching;
  const error = treeQuery.error || machineCandidatesQuery.error;
  const rootNodeCount = treeQuery.data?.length ?? 0;
  const totalNodeCount = countTreeNodes(treeQuery.data);
  const flatNodes = useMemo(() => flattenAssetNodes(treeQuery.data), [treeQuery.data]);
  // The table's rows. The include-inactive switch above the tabs already decided which nodes
  // exist; this only decides which of them are on screen.
  const tableNodes = useMemo(
    () => filterAssetNodes(flatNodes, searchText, { intl }),
    [flatNodes, searchText, intl],
  );
  // The tree's reading of the same query: the matches plus the path down to them, or null when
  // nothing is typed.
  const treeVisibleIds = useMemo(
    () => getTreeSearchVisibleIds(flatNodes, searchText, { intl }),
    [flatNodes, searchText, intl],
  );
  // While searching, everything on screen is open unless it was collapsed during this search.
  const treeExpandedIds = useMemo(
    () => (treeVisibleIds ? new Set([...treeVisibleIds].filter((id) => !searchCollapsedIds.has(id))) : expandedIds),
    [expandedIds, searchCollapsedIds, treeVisibleIds],
  );
  const hasNoTreeMatch = treeVisibleIds?.size === 0;
  const selectedNode = useMemo(() => flatNodes.find((node) => node.id === selectedNodeId) ?? null, [flatNodes, selectedNodeId]);
  const selectedParentNode = useMemo(() => flatNodes.find((node) => node.id === selectedNode?.parentAssetNodeId) ?? null, [flatNodes, selectedNode]);
  const canAddNode = Boolean(selectedNode?.isActive && getAllowedChildTypeOptions(selectedNode).length > 0);
  const canDeactivateNode = Boolean(
    selectedNode?.isActive &&
    selectedNode.nodeType !== 1 &&
    !(selectedNode.children ?? []).some((child) => child.isActive)
  );
  const linkedMachine = useMemo(() => {
    if (!selectedNode) return null;

    if (selectedNode.linkedMachineId) {
      const machine = (machineCandidatesQuery.data ?? []).find((candidate) => candidate.id === selectedNode.linkedMachineId);

      return {
        id: selectedNode.linkedMachineId,
        machineName: selectedNode.linkedMachineName ?? machine?.machineName ?? `Machine ${selectedNode.linkedMachineId}`,
      };
    }

    return (machineCandidatesQuery.data ?? []).find((machine) => machine.assetNodeId === selectedNode.id) ?? null;
  }, [machineCandidatesQuery.data, selectedNode]);
  const isMutating = [createMutation, updateMutation, reparentMutation, deactivateMutation, assignMachineMutation, unassignMachineMutation]
    .some((mutation) => mutation.isPending || mutation.isLoading);

  useEffect(() => {
    if (isLoading || error) return;

    if (!flatNodes.length) {
      if (selectedNodeId !== null) setSelectedNodeId(null);
      return;
    }

    if (!selectedNode) {
      setSelectedNodeId(flatNodes[0].id);
    }
  }, [error, flatNodes, isLoading, selectedNode, selectedNodeId]);

  const closeForm = () => {
    setFormMode(null);
    setFormParentNode(null);
  };

  const handleRetry = () => {
    refreshAssetData();
  };

  const handleAddNode = () => {
    if (!canAddNode) return;
    setFormParentNode(selectedNode);
    setFormMode("create");
  };

  // The one create path that does not start from a selected node, because on an empty
  // hierarchy there is nothing to select.
  const handleAddRootNode = () => {
    setFormParentNode(null);
    setFormMode("createRoot");
  };

  const handleEditNode = () => {
    if (!selectedNode) return;
    setFormParentNode(selectedParentNode);
    setFormMode("edit");
  };

  const findFlatNode = (id) => flatNodes.find((node) => node.id === id) ?? null;

  const revealPath = (ids) => {
    setExpandedIds((current) => expandIds(current, ids));
  };

  // Opens the branch a node sits in, so it can't end up selected but hidden inside a collapsed
  // one. Looked up by id because the tree hands over its own nested node, which has no ancestry.
  const revealNode = (id) => {
    revealPath(findFlatNode(id)?.ancestorIds ?? []);
  };

  // For a node that is not in flatNodes yet (just created, or just moved) — opening its parent
  // is what puts it on screen once the refetch lands.
  const revealChildrenOf = (parentId) => {
    const parent = findFlatNode(parentId);
    if (parent) revealPath([...parent.ancestorIds, parent.id]);
  };

  // Membership in searchCollapsedIds means closed, so the same flip works for both sets.
  const handleToggleNode = (id) => {
    if (treeVisibleIds) {
      setSearchCollapsedIds((current) => toggleExpandedId(current, id));
      return;
    }
    setExpandedIds((current) => toggleExpandedId(current, id));
  };

  // Behind both tabs' search boxes and both clear buttons, so no way of changing the query can
  // skip reopening what the last one had collapsed.
  const handleSearchChange = (value) => {
    setSearchText(value);
    setSearchCollapsedIds(new Set());
  };

  const handleSelectNode = (node) => {
    setSelectedNodeId(node.id);
    revealNode(node.id);
  };

  // The table has no panel beside it, so selecting is also opening. The tree keeps the plain
  // version above: there, the details are already on screen. Still reveals, so the row picked
  // here is visible on switching back to the tree.
  const handleSelectNodeFromTable = (node) => {
    setSelectedNodeId(node.id);
    revealNode(node.id);
    setIsDetailsOpen(true);
  };

  const handleSaveNode = async (formState) => {
    if (!formState.name?.trim()) {
      pushErrorMessage(intl.formatMessage(messages.toastNameRequired));
      return;
    }

    // The server checks this too, but answers in English; this says it in the user's language.
    if (!isValidModelYear(formState.modelYear)) {
      pushErrorMessage(intl.formatMessage(messages.toastModelYearInvalid, {
        minYear: MIN_MODEL_YEAR,
        maxYear: getMaxModelYear(),
      }));
      return;
    }

    // The API replaces all four together, so every save sends all four: a blank one clears it.
    // An update takes them as one object, since the API leaves the nameplate alone when that is
    // missing; a create takes them flat.
    const nameplate = {
      manufacturer: formState.manufacturer,
      model: formState.model,
      modelYear: formState.modelYear,
      serialNumber: formState.serialNumber,
    };

    try {
      if (formMode === "edit") {
        await updateMutation.mutateAsync({
          id: formState.id,
          assetNode: {
            companyKey,
            name: formState.name,
            description: formState.description,
            sortOrder: formState.sortOrder,
            isActive: formState.isActive,
            nameplate,
          },
        });
        pushNotification(intl.formatMessage(messages.toastNodeSaved));
      } else {
        const createdNode = await createMutation.mutateAsync({
          companyKey,
          // Null is the payload the API reads as "root", so it has to survive as null
          // rather than being coerced into a falsy id.
          parentAssetNodeId: formMode === "createRoot" ? null : formState.parentAssetNodeId,
          nodeType: formState.nodeType,
          name: formState.name,
          description: formState.description,
          sortOrder: formState.sortOrder,
          machineId: formState.machineId || null,
          ...nameplate,
        });
        setSelectedNodeId(createdNode.id);
        if (formMode !== "createRoot") revealChildrenOf(formState.parentAssetNodeId);
        pushNotification(intl.formatMessage(messages.toastNodeCreated));
      }

      closeForm();
      await refreshAssetData();
    } catch (mutationError) {
      pushErrorMessage(formatCmmsError(mutationError, intl.formatMessage(cmmsMessages.errorGeneric)));
    }
  };

  /*
   * The in-place edits' save: the description tab and the machine card's nameplate rows. Each
   * changes one field, but the PATCH replaces every field it is given, a blank one included, so
   * the rest go back exactly as the node has them. Resolves to whether it saved, so the caller can
   * keep the user's text open when it did not.
   */
  const handleUpdateNode = async (changes, successMessage = messages.toastNodeSaved) => {
    if (!selectedNode) return false;

    const { description = selectedNode.description, ...nameplateChanges } = changes;

    try {
      await updateMutation.mutateAsync({
        id: selectedNode.id,
        assetNode: {
          companyKey,
          name: selectedNode.name,
          description,
          sortOrder: selectedNode.sortOrder,
          isActive: selectedNode.isActive,
          nameplate: {
            manufacturer: selectedNode.manufacturer,
            model: selectedNode.model,
            modelYear: selectedNode.modelYear,
            serialNumber: selectedNode.serialNumber,
            ...nameplateChanges,
          },
        },
      });
      pushNotification(intl.formatMessage(successMessage));
      await refreshAssetData();
      return true;
    } catch (mutationError) {
      pushErrorMessage(formatCmmsError(mutationError, intl.formatMessage(cmmsMessages.errorGeneric)));
      return false;
    }
  };

  // True from the moment an in-place save starts until the tree has been re-read. A save in that
  // window would be built from the node as it was before the last one.
  const isUpdatingNode = updateMutation.isPending || updateMutation.isLoading || treeQuery.isFetching;

  const handleMoveNode = async (newParentAssetNodeId) => {
    if (!selectedNode || !newParentAssetNodeId) return;

    try {
      await reparentMutation.mutateAsync({ id: selectedNode.id, newParentAssetNodeId });
      revealChildrenOf(newParentAssetNodeId);
      setIsReparentOpen(false);
      pushNotification(intl.formatMessage(messages.toastNodeMoved));
      await refreshAssetData();
    } catch (mutationError) {
      pushErrorMessage(formatCmmsError(mutationError, intl.formatMessage(cmmsMessages.errorGeneric)));
    }
  };

  const handleDeactivateNode = () => {
    if (!selectedNode || !canDeactivateNode) return;

    setConfirmation({
      title: intl.formatMessage(messages.confirmDeactivateTitle),
      message: intl.formatMessage(messages.confirmDeactivateMessage, { name: selectedNode.name }),
      confirmText: intl.formatMessage(messages.deactivate),
      onConfirm: async () => {
        try {
          await deactivateMutation.mutateAsync(selectedNode.id);
          setSelectedNodeId(null);
          // Explicitly, not by the drawer's own null-node guard: the effect below re-selects the
          // first node as soon as the selection is cleared, which would otherwise leave the
          // drawer open on a node the user never asked about.
          setIsDetailsOpen(false);
          setConfirmation(null);
          pushNotification(intl.formatMessage(messages.toastNodeDeactivated));
          await refreshAssetData();
        } catch (mutationError) {
          pushErrorMessage(formatCmmsError(mutationError, intl.formatMessage(cmmsMessages.errorGeneric)));
        }
      },
    });
  };

  const handleAssignMachine = async (machineId) => {
    if (!selectedNode || !machineId) return;

    try {
      await assignMachineMutation.mutateAsync({ assetNodeId: selectedNode.id, machineId });
      pushNotification(intl.formatMessage(messages.toastMachineLinked));
      await refreshAssetData();
    } catch (mutationError) {
      pushErrorMessage(formatCmmsError(mutationError, intl.formatMessage(cmmsMessages.errorGeneric)));
    }
  };

  const handleUnassignMachine = (machine) => {
    setConfirmation({
      title: intl.formatMessage(messages.confirmUnlinkTitle),
      message: intl.formatMessage(messages.confirmUnlinkMessage, { name: machine.machineName }),
      confirmText: intl.formatMessage(messages.machineLinkUnlink),
      onConfirm: async () => {
        try {
          await unassignMachineMutation.mutateAsync(machine.id);
          setConfirmation(null);
          pushNotification(intl.formatMessage(messages.toastMachineUnlinked));
          await refreshAssetData();
        } catch (mutationError) {
          pushErrorMessage(formatCmmsError(mutationError, intl.formatMessage(cmmsMessages.errorGeneric)));
        }
      },
    });
  };

  return (
    <Box sx={getPageShellSx(cmmsColors)}>
      <Box sx={getAssetHierarchyHeaderSx(cmmsColors)}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}>
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.5 }}>
            <AccountTreeIcon sx={getPageHeaderIconSx(cmmsColors)} />
            <Box>
              <Typography variant={cmmsTypeScale.pageTitle} sx={getPageTitleSx(cmmsColors)}>
                <FormattedMessage {...messages.pageTitle} />
              </Typography>
              <Typography variant={cmmsTypeScale.pageSubtitle} sx={getPageSubtitleSx(cmmsColors)}>
                <FormattedMessage {...messages.pageSubtitle} />
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Box sx={getStatisticTileSx(cmmsColors)}>
              <Typography variant={cmmsTypeScale.statLabel} sx={getStatLabelSx(cmmsColors)}>
                <FormattedMessage {...messages.statRootNodes} />
              </Typography>
              <Typography variant={cmmsTypeScale.statValue} sx={getStatValueSx(cmmsColors)}>
                {rootNodeCount}
              </Typography>
            </Box>
            <Box sx={getStatisticTileSx(cmmsColors)}>
              <Typography variant={cmmsTypeScale.statLabel} sx={getStatLabelSx(cmmsColors)}>
                <FormattedMessage {...messages.statTotalNodes} />
              </Typography>
              <Typography variant={cmmsTypeScale.statValue} sx={getStatValueSx(cmmsColors)}>
                {totalNodeCount}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ mt: 2, pt: 2, borderTop: `1px solid ${cmmsColors.border}` }}>
          <AssetHierarchyToolbar
            canAddNode={canAddNode}
            includeInactive={includeInactive}
            onAddNode={handleAddNode}
            onIncludeInactiveChange={setIncludeInactive}
          />
        </Box>
      </Box>

      {/*
        * Below the toolbar rather than above it, because the toolbar is shared: Add and Include
        * inactive both mean the same thing on either tab, and Include inactive in
        * particular decides which nodes exist rather than how they are drawn. Same shape as
        * cmmsSettings' tab bar. Outside the header and with no gap under it, so the selected tab
        * joins the tile below, as on the machine page.
        */}
      <Tabs
        value={tab}
        onChange={(_event, value) => setTab(value)}
        variant="scrollable"
        scrollButtons="auto"
        sx={{ ...getTabsSx(cmmsColors), mt: 2 }}
      >
        <Tab disableRipple label={intl.formatMessage(messages.tabTree)} />
        <Tab disableRipple label={intl.formatMessage(messages.tabTable)} />
      </Tabs>

      {tab === 0 &&
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", lg: "minmax(320px, 420px) 1fr" },
          alignItems: "start",
          gap: 2,
        }}
      >
        {/*
          * 345px is everything above the columns (top bar, the page container's margin and
          * padding, header, toolbar, tabs) plus the container's padding and margin below them, so
          * the columns end at the bottom of the window and the page itself does not scroll.
          */}
        <Box
          sx={{
            ...getAssetHierarchyPanelSx(cmmsColors),
            height: { xs: "auto", lg: "calc(100vh - 345px)" },
            minHeight: 520,
          }}
        >
          {/* Outside the scrolling box below, so it stays put while the tree scrolls under it. */}
          <Box sx={{ px: 2, pt: 2 }}>
            <TextField
              size="small"
              fullWidth
              value={searchText}
              onChange={(event) => handleSearchChange(event.target.value)}
              label={intl.formatMessage(messages.filterSearch)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {isLoading &&
            // Indented widths so the placeholder reads as a tree rather than a list.
            <CmmsListSkeleton rows={7} height={26} sx={{ "& > *:nth-of-type(even)": { ml: 3 } }} />
          }
          {!isLoading && error &&
            <CmmsErrorState error={error} onRetry={handleRetry} isRetrying={isRetrying} dense />
          }
          {!isLoading && !error && flatNodes.length === 0 &&
            <CmmsEmptyState
              icon={<AccountTreeIcon />}
              title={intl.formatMessage(messages.noAssetNodes)}
              hint={intl.formatMessage(messages.emptyTreeHint)}
              dense
              action={
                // Every other add path hangs off a selected node, which an empty tree does
                // not have. Same gate as the rest of the write controls: POST is
                // Cmms_Manager server-side, so anyone else would only get a 403.
                <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
                  <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddRootNode} sx={getContainedButtonSx(cmmsColors)}>
                    <FormattedMessage {...messages.createRootNode} />
                  </Button>
                </HideIfNoRole>
              }
            />
          }
          {!isLoading && !error && flatNodes.length > 0 && hasNoTreeMatch &&
            <CmmsEmptyState
              icon={<FilterAltOffIcon />}
              title={intl.formatMessage(messages.tableNoMatchTitle)}
              hint={intl.formatMessage(messages.tableNoMatchHint)}
              dense
              action={
                <Button size="small" variant="outlined" color="secondary" onClick={() => handleSearchChange("")}>
                  <FormattedMessage {...messages.filterClear} />
                </Button>
              }
            />
          }
          {!isLoading && !error && flatNodes.length > 0 && !hasNoTreeMatch &&
            <Box>
              <AssetHierarchyTree
                nodes={treeQuery.data}
                selectedNodeId={selectedNodeId}
                onSelectNode={handleSelectNode}
                expandedIds={treeExpandedIds}
                onToggleNode={handleToggleNode}
                visibleIds={treeVisibleIds}
              />
            </Box>
          }
          </Box>
        </Box>

        {/*
          * No panel of its own: the details are a column of section tiles, and in OEE tiles stand
          * on the page with a gap between them rather than inside another box.
          */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            overflow: "hidden",
            height: { xs: "auto", lg: "calc(100vh - 345px)" },
            minHeight: 520,
          }}
        >
          <Box sx={{ flex: 1, overflow: "auto" }}>
            {isLoading &&
              <CmmsListSkeleton rows={6} height={30} />
            }
            {!isLoading && error &&
              <CmmsErrorState error={error} onRetry={handleRetry} isRetrying={isRetrying} dense />
            }
            {!isLoading && !error &&
              <AssetNodeDetailsPanel
                canAddChild={canAddNode}
                canDeactivate={canDeactivateNode}
                companyKey={companyKey}
                isUpdatingNode={isUpdatingNode}
                linkedMachine={linkedMachine}
                machineOptions={machineCandidatesQuery.data ?? []}
                onAddChild={handleAddNode}
                onAssignMachine={handleAssignMachine}
                onDeactivate={handleDeactivateNode}
                onEdit={handleEditNode}
                onMove={() => setIsReparentOpen(true)}
                onUnassignMachine={handleUnassignMachine}
                onUpdateNode={handleUpdateNode}
                selectedNode={selectedNode}
              />
            }
          </Box>
        </Box>
      </Box>
      }

      {tab === 1 &&
        <Box sx={{ ...getAssetHierarchyPanelSx(cmmsColors), p: 2 }}>
          <TextField
            size="small"
            sx={{ width: { xs: "100%", sm: 420 }, mb: 2 }}
            value={searchText}
            onChange={(event) => handleSearchChange(event.target.value)}
            label={intl.formatMessage(messages.filterSearch)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
          />

          {error &&
            <CmmsErrorState error={error} onRetry={handleRetry} isRetrying={isRetrying} />
          }
          {!error &&
            <AssetHierarchyTable
              nodes={tableNodes}
              // First load only, so a background refetch does not replace the table with a
              // skeleton.
              isLoading={treeQuery.isLoading}
              onSelectNode={handleSelectNodeFromTable}
              emptyState={
                // A search that found nothing and a hierarchy that holds nothing are different
                // problems with different fixes — the register's two-branch pattern.
                searchText.trim()
                  ? <CmmsEmptyState
                      icon={<FilterAltOffIcon />}
                      title={intl.formatMessage(messages.tableNoMatchTitle)}
                      hint={intl.formatMessage(messages.tableNoMatchHint)}
                      action={
                        <Button size="small" variant="outlined" color="secondary" onClick={() => handleSearchChange("")}>
                          <FormattedMessage {...messages.filterClear} />
                        </Button>
                      }
                    />
                  : <CmmsEmptyState
                      icon={<AccountTreeIcon />}
                      title={intl.formatMessage(messages.noAssetNodes)}
                      hint={intl.formatMessage(messages.emptyTreeHint)}
                      action={
                        <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
                          <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddRootNode} sx={getContainedButtonSx(cmmsColors)}>
                            <FormattedMessage {...messages.createRootNode} />
                          </Button>
                        </HideIfNoRole>
                      }
                    />
              }
            />
          }
        </Box>
      }

      {/*
        * The table tab's stand-in for the panel beside the tree — the same component with the
        * same props, so the two tabs can do the same things to a node.
        */}
      <AssetNodeDetailsDrawer
        open={isDetailsOpen}
        onClose={() => setIsDetailsOpen(false)}
        canAddChild={canAddNode}
        canDeactivate={canDeactivateNode}
        companyKey={companyKey}
        isUpdatingNode={isUpdatingNode}
        linkedMachine={linkedMachine}
        machineOptions={machineCandidatesQuery.data ?? []}
        onAddChild={handleAddNode}
        onAssignMachine={handleAssignMachine}
        onDeactivate={handleDeactivateNode}
        onEdit={handleEditNode}
        onMove={() => setIsReparentOpen(true)}
        onUnassignMachine={handleUnassignMachine}
        onUpdateNode={handleUpdateNode}
        selectedNode={selectedNode}
      />

      <AssetNodeFormDialog
        mode={formMode}
        open={Boolean(formMode)}
        parentNode={formParentNode}
        selectedNode={selectedNode}
        machineOptions={machineCandidatesQuery.data ?? []}
        onClose={closeForm}
        onSave={handleSaveNode}
      />
      <ReparentDialog
        nodes={treeQuery.data}
        open={isReparentOpen}
        selectedNode={selectedNode}
        onClose={() => setIsReparentOpen(false)}
        onMove={handleMoveNode}
      />
      <AxionControls.AxionConfirmation
        open={Boolean(confirmation)}
        title={confirmation?.title}
        message={confirmation?.message}
        confirmText={confirmation?.confirmText ?? intl.formatMessage(cmmsMessages.confirm)}
        cancelText={intl.formatMessage(cmmsMessages.cancel)}
        onConfirm={confirmation?.onConfirm}
        onCancel={() => setConfirmation(null)}
      />
      <AxionControls.AxionBackdrop open={isMutating} />
    </Box>
  );
}
